/**
 * TISA COINS - STUDENT DASHBOARD
 *
 * Main dashboard for students to view their:
 * - XP, Level, and Guild
 * - Coin vault and bank accounts
 * - Active and available quests
 * - Earned badges
 * - Recent activity
 */

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { TISAUser, Quest } from "@/types/tisa-coins";
import { computeStudentStats } from "@/lib/tisa-helpers";
import { getGuildById, getNextLevelPerks } from "@/config/tisa-settings";

// Components
import { StudentHeroCard } from "./components/StudentHeroCard";
import { QuickStatsGrid } from "./components/QuickStatsGrid";
import { ActiveQuestsSection } from "./components/ActiveQuestsSection";
import { AvailableQuestsSection } from "./components/AvailableQuestsSection";
import { BankSection } from "./components/BankSection";
import { BadgesSection } from "./components/BadgesSection";
import { RecentActivitySection } from "./components/RecentActivitySection";
import { ShopSection } from "./components/ShopSection";
import { PurchaseModal } from "./components/PurchaseModal";
import { purchaseItem } from "@/lib/shop";
import type { ShopItem } from "@/config/tisa-settings";
import { useToast } from "@/hooks/use-toast";

export default function StudentCoinsPage() {
  const { user: authUser } = useAuth();
  const { toast } = useToast();
  const [user, setUser] = useState<TISAUser | null>(null);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);

  // Load user data and quests
  useEffect(() => {
    if (!authUser) {
      setError("Please log in to view your TISA Coins");
      setLoading(false);
      return;
    }
    loadUserData();
    loadQuests();
  }, [authUser]);

  const loadUserData = async () => {
    if (!authUser) return;

    try {
      // Students are in children table
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("id", authUser.id)
        .single();

      if (error) throw error;
      setUser(data as unknown as TISAUser);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load user data");
      console.error("Error loading user data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadQuests = async () => {
    if (!authUser) return;

    try {
      // Get student's guild_id first (students are in children table)
      const { data: userData } = await supabase
        .from("children")
        .select("guild_id")
        .eq("id", authUser.id)
        .single();

      // Load active quests (all quests or guild-specific)
      const { data, error } = await supabase
        .from("quests")
        .select("*")
        .eq("active", true)
        .or(`guild_id.is.null,guild_id.eq.${userData?.guild_id || "none"}`);

      if (error) throw error;
      setQuests(data as Quest[]);
    } catch (err: unknown) {
      const error =
        err instanceof Error ? err : new Error("Failed to load quests");
      console.error("Error loading quests:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-white text-xl font-cinzel">
            Loading your legend...
          </p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-2xl font-cinzel mb-4">
            ❌ Error loading dashboard
          </p>
          <p className="text-gray-300">{error || "User not found"}</p>
        </div>
      </div>
    );
  }

  // Compute all stats
  const stats = computeStudentStats(user);
  const guild = user.guild_id ? (getGuildById(user.guild_id) ?? null) : null;
  const levelInfo = getNextLevelPerks(stats.total_xp);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Hero Card - XP, Level, Progress */}
        <StudentHeroCard stats={stats} levelInfo={levelInfo} guild={guild} />

        {/* Quick Stats Grid - Vault, Total XP, Badges, Quests */}
        <QuickStatsGrid stats={stats} />

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Left Column - Quests */}
          <div className="lg:col-span-2 space-y-8">
            <ActiveQuestsSection
              user={user}
              quests={quests}
              onRefresh={loadUserData}
            />

            <AvailableQuestsSection
              user={user}
              quests={quests}
              onRefresh={loadUserData}
            />
          </div>

          {/* Right Column - Bank & Badges */}
          <div className="space-y-8">
            <BankSection user={user} onRefresh={loadUserData} />

            <BadgesSection user={user} />

            <RecentActivitySection user={user} />
          </div>
        </div>

        {/* Shop Section - Full Width */}
        <div className="mt-12">
          <ShopSection
            vaultBalance={stats.vault_balance}
            studentLevel={stats.current_level}
            onPurchase={(item) => setSelectedItem(item)}
          />
        </div>
      </main>

      {/* Purchase Modal */}
      {selectedItem && (
        <PurchaseModal
          item={selectedItem}
          vaultBalance={stats.vault_balance}
          onConfirm={handlePurchase}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );

  async function handlePurchase(item: ShopItem) {
    if (!authUser) return;

    try {
      const result = await purchaseItem(authUser.id, {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        category: item.category,
        rarity: item.rarity || "common",
      });

      if (result.success && result.data) {
        toast({
          title: "✅ Purchase Successful!",
          description: result.data.message,
          variant: "default",
        });
        setSelectedItem(null);
        await loadUserData(); // Refresh balance
      } else {
        throw new Error(result.error || "Purchase failed");
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Purchase failed");
      toast({
        title: "❌ Purchase Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  }
}
